import PaymentDetail from './PaymentDetail'
import SignData, { IBackendParamsCreateContract, TypeSignData } from './SignData'
import BigNumber from 'bignumber.js'
import { EVM_BLOCKCHAIN, getChainIdByBlockchain, getZeroAddressByWalletGroup } from '@/constants/blockchain'
import { ContractVersion } from '@/constants/contracts'
import { WNATIVE_BY_BLOCKCHAIN } from '@/constants/currency'
import { AccountTypes } from '@/constants/user/accountTypes'
import { DATE_TIME_FORMAT_BY_MERIDIEM } from '@/constants/utils/date'
import Currency from '@/models-ts/Currency'
import Rate from '@/models-ts/Rate'
import Avatar from '@/models-ts/user/Avatar'
import Wallet from '@/models-ts/Wallet'
import LaborXContractV2, { getLaborXContractV2Async, ICreateContractParamsV2 } from '@/servicies-ts/blockchain/laborx-contract-v2'
import { getApprove } from '@/servicies-ts/pay-details/pay-details-erc20'
import { getPaymentDetailsCreateContractV2 } from '@/servicies-ts/pay-details/pay-details-lx-v2'
import { getAmountWithCustomerFeeV2, getBalanceErrors } from '@/utils-ts/contract'
import { convertFromUsd, getPreferredCurrency } from '@/utils-ts/currencies'
import { addSeconds, formatDate } from '@/utils/date'

export type IApprove = {
  spender: string
  amount: string
  details?: PaymentDetail[]
}

export type FreelancerSignData = {
  id: string | number
  name: string
  avatar: Avatar
  type: AccountTypes
  wallet: string
}

export type MissingBalanceToSign = {
  value: BigNumber
  currency: Currency
}

type PropsType = Required<Omit<CreateContractSignDataV2, 'freelancerWallet'>> &
  ConstructorParameters<typeof SignData> & { freelancerWallet?: Wallet }

type GetInstancePropsType = {
  version: ContractVersion
  scId: string
  preferredCurrencies: Currency[]
  deadline: number
  wallet: Wallet
  rates: Rate[]
  amountUsd: BigNumber
  freelancer: FreelancerSignData
  gigOfferId?: string
  jobId?: number
  name: string
  detailsRoute: CreateContractSignDataV2['detailsRoute']
  successCb: () => void
  errorCb: () => void
  cancelCb: () => void
}

export default class CreateContractSignDataV2 extends SignData<'createContract', ICreateContractParamsV2, IBackendParamsCreateContract> {
  type = TypeSignData.CreateContract
  detailsRoute: { name: string; params: {} }
  amountUsd: BigNumber
  missingBalance?: MissingBalanceToSign[]
  preferredCurrencies: Currency[]
  approve?: IApprove // ToDo: move to CreateContractSignData

  static async createInstance({
    version,
    preferredCurrencies,
    deadline,
    wallet,
    rates,
    gigOfferId,
    jobId,
    ...props
  }: GetInstancePropsType): Promise<CreateContractSignDataV2> {
    const TWO_DAYS = 172800
    const date = formatDate(addSeconds(Date.now(), deadline + TWO_DAYS), DATE_TIME_FORMAT_BY_MERIDIEM)
    const currency = getPreferredCurrency(preferredCurrencies, wallet.group)!
    const amount = convertFromUsd({
      value: props.amountUsd,
      currency: currency,
      rates,
    }).multipliedBy(currency.baseUnits)
    const contract: any = await getLaborXContractV2Async({ chainId: getChainIdByBlockchain(currency.blockchain) })
    const amountAndFee: BigNumber = await getAmountWithCustomerFeeV2(amount, contract)
    let payableAmount: string = currency.isBaseCurrency ? amountAndFee.toFixed(0) : '0'
    const deadlineBN: BigNumber = new BigNumber(deadline || 0)
    const postfix: 'Eth' | 'Erc20' = currency.isBaseCurrency ? 'Eth' : 'Erc20'
    const freelancerAddress = props.freelancer.wallet
    const methodArgs: ICreateContractParamsV2 = {
      mode: 'encodeABI',
      from: wallet.address,
      contractId: props.scId,
      freelancer: freelancerAddress,
      disputer: getZeroAddressByWalletGroup(wallet.group),
      token: (currency.isBaseCurrency ? WNATIVE_BY_BLOCKCHAIN[currency.blockchain] : currency.address)!,
      amount: amount.toFixed(0),
      duration: deadlineBN.toFixed(0),
      percentToBaseConvert: '0',
    }
    let gasPrice: string | null = null
    let paymentDetails: PaymentDetail[] = await getPaymentDetailsCreateContractV2(
      methodArgs,
      contract,
      wallet,
      currency,
      payableAmount,
      gasPrice
    )
    gasPrice = await contract.web3Instance.eth.getGasPrice()
    const { errors, missingBalance } = await getBalanceErrors(currency, wallet, paymentDetails, amountAndFee)

    let approve = await getApprove(
      currency,
      getChainIdByBlockchain(currency.blockchain),
      wallet,
      gasPrice || '0',
      contract.contractInstance.options.address,
      amountAndFee.toFixed(0) || '0'
    )

    const backendParams: IBackendParamsCreateContract = {
      postfix,
      jobId,
      gigOfferId,
      scId: props.scId,
      blockchain: currency.blockchain,
      customerWallet: wallet.address || '',
      freelancerWallet: freelancerAddress,
      backendCurrencyId: currency.backendId,
      deadline: deadlineBN.toNumber(),
    }
    return new CreateContractSignDataV2({
      ...props,
      version,
      wallet,
      freelancer: props.freelancer,
      deadline,
      payableAmount,
      amountAndFee,
      // @ts-ignore
      approve,
      currency,
      preferredCurrencies,
      blockchain: currency.blockchain,
      contractName: 'LaborXContractV1',
      paymentDetails,
      contractLink: contract.contractLink(currency.blockchain),
      gasPrice,
      errors,
      missingBalance,
      backendParams,
      methodName: 'createContract',
      methodArgs,
      title: 'Create Contract',
      description: `Create a contract with a freelancer.<br>
Funds will be locked in escrow and can be released after <b>${date}</b>.<br>`,
    })
  }

  constructor({ detailsRoute, amountUsd, missingBalance, preferredCurrencies, approve, ...props }: PropsType) {
    super(props as SignData<'createContract', ICreateContractParamsV2, IBackendParamsCreateContract>)
    this.detailsRoute = detailsRoute
    this.amountUsd = amountUsd
    this.missingBalance = missingBalance
    this.preferredCurrencies = preferredCurrencies
    this.methodName = 'createContract'
    this.approve = approve
    this.setGasPrice = async function (gasPrice: string) {
      this.gasPrice = gasPrice
      const chainId = getChainIdByBlockchain(this.currency?.blockchain)
      let contract: any = await getLaborXContractV2Async({ chainId })
      this.paymentDetails = await getPaymentDetailsCreateContractV2(
        this.methodArgs,
        contract,
        this.wallet,
        this.currency,
        this.payableAmount,
        this.gasPrice
      )
      const { errors, missingBalance } = await getBalanceErrors(this.currency, this.wallet, this.paymentDetails, this.amountAndFee!)
      this.errors = errors
      this.missingBalance = missingBalance
    }
  }

  async setCurrency(currency: Currency, rates: Rate[]) {
    this.currency = currency
    let contract: LaborXContractV2 = await getLaborXContractV2Async({
      chainId: getChainIdByBlockchain(currency.blockchain),
    })
    const amount: BigNumber = convertFromUsd({
      value: this.amountUsd,
      currency,
      rates,
    }).multipliedBy(currency.baseUnits)
    this.methodArgs.amount = amount.toFixed(0)
    this.methodArgs.token = (currency.isBaseCurrency ? WNATIVE_BY_BLOCKCHAIN[currency.blockchain] : currency.address)!
    if (currency.blockchain !== this.blockchain) {
      const contractEvm = contract as LaborXContractV2
      this.gasPrice = await contractEvm.web3Instance.eth.getGasPrice()
      this.blockchain = currency.blockchain
    }
    this.backendParams.backendCurrencyId = currency.backendId
    this.backendParams.blockchain = currency.blockchain
    this.contractLink = contract.contractLink(currency.blockchain)
    await this.initContractData(contract, rates)
  }

  async setWallet(wallet: Wallet, rates: Rate[]) {
    let contract: any
    this.currency = this.preferredCurrencies.find((currency: Currency) => {
      return EVM_BLOCKCHAIN.includes(currency.blockchain)
    }) as Currency
    contract = await getLaborXContractV2Async({ chainId: getChainIdByBlockchain(this.currency.blockchain) })
    this.gasPrice = await contract.web3Instance.eth.getGasPrice()
    this.methodArgs.token = this.currency.contractTokenAddress
    this.methodArgs.disputer = getZeroAddressByWalletGroup(wallet.group)
    this.backendParams.blockchain = this.currency.blockchain
    this.blockchain = this.currency.blockchain
    const freelancerAddress = this.freelancer!.wallet
    this.methodArgs.freelancer = freelancerAddress
    this.backendParams.freelancerWallet = freelancerAddress
    this.wallet = wallet
    this.methodArgs.from = wallet.address
    this.backendParams.customerWallet = wallet.address
    await this.initContractData(contract, rates)
  }

  async initContractData(contract: LaborXContractV2, rates: Rate[]) {
    const currency = this.currency
    const wallet = this.wallet
    const amount = convertFromUsd({
      value: this.amountUsd,
      currency: currency,
      rates,
    }).multipliedBy(currency.baseUnits)
    this.methodArgs.amount = amount.toFixed(0)
    this.amountAndFee = await getAmountWithCustomerFeeV2(amount, contract)
    this.payableAmount = this.currency.isBaseCurrency ? this.amountAndFee.toFixed(0) : '0'
    this.approve = await getApprove(
      currency,
      getChainIdByBlockchain(currency.blockchain),
      this.wallet,
      this.gasPrice || '0',
      contract.contractInstance.options.address,
      this.amountAndFee.toFixed(0)
    )
    this.paymentDetails = await getPaymentDetailsCreateContractV2(
      this.methodArgs,
      contract,
      this.wallet,
      currency,
      this.payableAmount,
      this.gasPrice || '0'
    )
    const { errors, missingBalance } = await getBalanceErrors(currency, wallet, this.paymentDetails, this.amountAndFee)
    this.errors = errors
    this.missingBalance = missingBalance
  }
}
